import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { getChampionImage } from "../../utils/team";

const T = {
    charging: 700,
    impact: 280,
    lunge1: 220,
    recoil1: 220,
    lunge2: 220,
    recoil2: 220,
    finalHold: 320,
    retreating: 700,
};

type Phase =
    | "charging"
    | "impact"
    | "lunge1"
    | "recoil1"
    | "lunge2"
    | "recoil2"
    | "finalHold"
    | "retreating";

interface Props {
    active: boolean;
    onComplete: () => void;
}

const SIZE = 150;
const OFFSCREEN = 900;

const trynByPhase: Record<Phase, { x: number; rotate: number }> = {
    charging: { x: -70, rotate: 0 },
    impact: { x: -135, rotate: -18 },
    lunge1: { x: -55, rotate: 14 },
    recoil1: { x: -110, rotate: -10 },
    lunge2: { x: -55, rotate: 14 },
    recoil2: { x: -105, rotate: -8 },
    finalHold: { x: -60, rotate: 0 },
    retreating: { x: -OFFSCREEN, rotate: 0 },
};

const malphByPhase: Record<Phase, { x: number; rotate: number }> = {
    charging: { x: 70, rotate: 0 },
    impact: { x: 135, rotate: 18 },
    lunge1: { x: 55, rotate: -14 },
    recoil1: { x: 110, rotate: 10 },
    lunge2: { x: 55, rotate: -14 },
    recoil2: { x: 105, rotate: 8 },
    finalHold: { x: 60, rotate: 0 },
    retreating: { x: OFFSCREEN, rotate: 0 },
};

const durations: Record<Phase, number> = {
    charging: T.charging,
    impact: T.impact,
    lunge1: T.lunge1,
    recoil1: T.recoil1,
    lunge2: T.lunge2,
    recoil2: T.recoil2,
    finalHold: T.finalHold,
    retreating: T.retreating,
};

const transitionForPhase = (p: Phase) => {
    if (p === "charging" || p === "retreating") {
        return { duration: durations[p] / 1000, ease: "easeIn" as const };
    }
    if (p === "impact") {
        return {
            duration: durations[p] / 1000,
            type: "spring" as const,
            stiffness: 320,
            damping: 14,
        };
    }
    return {
        duration: durations[p] / 1000,
        ease: "easeInOut" as const,
    };
};

export const TryndamereVsMalphite = ({ active, onComplete }: Props) => {
    const [phase, setPhase] = useState<Phase>("charging");

    useEffect(() => {
        if (!active) return;
        setPhase("charging");

        const timers: ReturnType<typeof setTimeout>[] = [];
        let t = 0;
        const sched = (ms: number, next: Phase) => {
            t += ms;
            timers.push(setTimeout(() => setPhase(next), t));
        };

        sched(T.charging, "impact");
        sched(T.impact, "lunge1");
        sched(T.lunge1, "recoil1");
        sched(T.recoil1, "lunge2");
        sched(T.lunge2, "recoil2");
        sched(T.recoil2, "finalHold");
        sched(T.finalHold, "retreating");

        t += T.retreating;
        timers.push(setTimeout(() => onComplete(), t));

        return () => timers.forEach(clearTimeout);
    }, [active, onComplete]);

    const flashActive = phase === "impact";

    return (
        <AnimatePresence>
            {active && (
                <motion.img
                    key="tryn"
                    src={getChampionImage("Tryndamere")}
                    alt="Tryndamere"
                    initial={{ x: -OFFSCREEN, rotate: 0, opacity: 1 }}
                    animate={trynByPhase[phase]}
                    transition={transitionForPhase(phase)}
                    exit={{ x: -OFFSCREEN, opacity: 0 }}
                    style={{
                        position: "fixed",
                        top: `calc(50% - ${SIZE / 2}px)`,
                        left: `calc(50% - ${SIZE / 2}px)`,
                        width: SIZE,
                        height: SIZE,
                        borderRadius: "50%",
                        border: "3px solid #c63e3e",
                        boxShadow: "0 0 36px rgba(198, 62, 62, 0.55)",
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                />
            )}
            {active && (
                <motion.img
                    key="malph"
                    src={getChampionImage("Malphite")}
                    alt="Malphite"
                    initial={{ x: OFFSCREEN, rotate: 0, opacity: 1 }}
                    animate={malphByPhase[phase]}
                    transition={transitionForPhase(phase)}
                    exit={{ x: OFFSCREEN, opacity: 0 }}
                    style={{
                        position: "fixed",
                        top: `calc(50% - ${SIZE / 2}px)`,
                        left: `calc(50% - ${SIZE / 2}px)`,
                        width: SIZE,
                        height: SIZE,
                        borderRadius: "50%",
                        border: "3px solid #5ba4cf",
                        boxShadow: "0 0 36px rgba(91, 164, 207, 0.55)",
                        zIndex: 9999,
                        pointerEvents: "none",
                    }}
                />
            )}
            {active && flashActive && (
                <motion.div
                    key="flash"
                    initial={{ opacity: 0, scale: 0.3 }}
                    animate={{ opacity: [0, 0.95, 0], scale: [0.3, 1.6, 2.8] }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        marginTop: -140,
                        marginLeft: -140,
                        width: 280,
                        height: 280,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,230,150,0.95) 0%, rgba(255,170,40,0.5) 40%, rgba(255,90,0,0) 70%)",
                        zIndex: 9998,
                        pointerEvents: "none",
                        mixBlendMode: "screen",
                    }}
                />
            )}
        </AnimatePresence>
    );
};
